/**
 * VoiceDoubtMode — voice-first interface for the Doubt Sidebar.
 *
 * Google-Assistant-style interaction:
 *   1. Tap the mic → starts listening (browser asks for mic permission
 *      once; persisted thereafter).
 *   2. Speak the question — live transcript updates underneath.
 *   3. Tap the mic again to stop → auto-submits to the parent's ask()
 *      callback (which hits the same /api/doubt/ask backend as text mode).
 *   4. Answer arrives → automatically read aloud by useSpeechNarration.
 *      The mic ring pulses while she's speaking.
 *   5. Tap mic during playback to interrupt and ask a follow-up.
 *
 * Why no new backend
 *   This component reuses the existing /doubt/ask endpoint. Voice mode
 *   is purely a UI shell around the same flow — same pricing, same
 *   conversation history, same answer markdown. The only "new" things
 *   are the browser SpeechRecognition API (free) and our existing
 *   useSpeechNarration hook (also free, just routed automatically).
 */
import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Volume2, Loader2, AlertCircle } from "lucide-react";
import useSpeechRecognition from "@/hooks/useSpeechRecognition";
import useSpeechNarration from "@/hooks/useSpeechNarration";
import LessonMarkdown from "@/components/LessonMarkdown";

export default function VoiceDoubtMode({
  ask,
  busy,
  lastTurn,
  ttsLang = "en-US",
  recLang = "en-US",
}) {
  const { start, stop, listening, transcript, supported, error: recError } = useSpeechRecognition({ lang: recLang });
  const { speak, stop: stopSpeak, speaking } = useSpeechNarration({ lang: ttsLang });
  // Track which turn we've already spoken so re-renders don't re-narrate.
  const spokenTurnIdRef = useRef(null);
  // Local error state for non-recogniser errors (e.g. parent ask() failure).
  const [submitting, setSubmitting] = useState(false);

  // Auto-speak the latest answer when a turn finalises.
  useEffect(() => {
    if (!lastTurn || !lastTurn.answer) return;
    if (spokenTurnIdRef.current === lastTurn.id) return;
    spokenTurnIdRef.current = lastTurn.id;
    // Strip markdown for natural narration. Reuses the same stripMd
    // semantics as LiveClassroomPanel without the dependency.
    const clean = _stripMd(lastTurn.answer);
    if (clean) speak(clean);
  }, [lastTurn, speak]);

  // Single button — toggles listening on/off. Tapping mid-playback
  // interrupts the TTS first, then opens the mic for a follow-up.
  const onMicTap = async () => {
    if (listening) {
      // Stop listening and submit if we captured anything meaningful.
      stop();
      const q = transcript.trim();
      if (q && !busy) {
        setSubmitting(true);
        try { await ask(q); } finally { setSubmitting(false); }
      }
      return;
    }
    // Not yet listening — kill any in-flight TTS first so the mic
    // doesn't pick up our own voice.
    if (speaking) stopSpeak();
    start();
  };

  // ─── Unsupported browser (mostly Firefox) ────────────────────────
  if (!supported) {
    return (
      <div className="p-5 text-center" data-testid="voice-unsupported">
        <AlertCircle className="mx-auto text-white/40" size={28} />
        <p className="mt-3 text-sm text-white/70 font-display">Voice mode not supported on this browser</p>
        <p className="mt-1 text-[11px] text-white/45">
          Try Chrome, Edge, or Safari for voice questions. You can keep using text mode in the meantime.
        </p>
      </div>
    );
  }

  // ─── Main UI ─────────────────────────────────────────────────────
  const isThinking = busy || submitting;
  const showWaveform = listening || speaking;
  return (
    <div className="p-5 flex flex-col items-center gap-5" data-testid="voice-doubt-mode">
      <div className="text-center">
        <div className="font-display text-sm text-white">
          {isThinking
            ? "Thinking…"
            : listening
              ? "I'm listening"
              : speaking
                ? "Answering…"
                : "Tap to ask"}
        </div>
        <div className="text-[11px] text-white/45 mt-1">
          {listening
            ? "Tap again when you're done."
            : speaking
              ? "Tap mic to interrupt and ask a follow-up."
              : "Hold a thought, then tap the mic and speak naturally."}
        </div>
      </div>

      {/* Big mic button with multi-ring pulse */}
      <div className="relative h-32 w-32 grid place-items-center">
        {showWaveform && (
          <>
            <span className="absolute inset-0 rounded-full bg-[#00FF9D]/15 animate-ping" />
            <span
              className="absolute inset-2 rounded-full bg-[#00FF9D]/10 animate-ping"
              style={{ animationDelay: "0.4s" }}
            />
          </>
        )}
        <button
          type="button"
          onClick={onMicTap}
          disabled={isThinking}
          className={`relative z-10 h-24 w-24 rounded-full grid place-items-center transition-all duration-200 ${
            listening
              ? "bg-rose-500 text-white shadow-[0_0_30px_rgba(244,63,94,0.6)]"
              : speaking
                ? "bg-[#00FF9D]/85 text-[#0B0E14] shadow-[0_0_24px_rgba(0,255,157,0.45)]"
                : isThinking
                  ? "bg-white/10 text-white/60 cursor-wait"
                  : "bg-[#00FF9D] text-[#0B0E14] hover:bg-[#7FFFCB] shadow-[0_0_18px_rgba(0,255,157,0.4)]"
          }`}
          aria-pressed={listening}
          aria-label={listening ? "Stop recording" : "Start recording"}
          data-testid="btn-voice-mic"
        >
          {isThinking ? (
            <Loader2 size={32} className="animate-spin" />
          ) : listening ? (
            <MicOff size={32} />
          ) : speaking ? (
            <Volume2 size={32} />
          ) : (
            <Mic size={32} />
          )}
        </button>
      </div>

      {/* Live transcript */}
      {(listening || transcript) && (
        <div
          className="w-full max-w-md min-h-[3rem] rounded-2xl border border-white/10 bg-white/[0.04] p-3 text-center"
          data-testid="voice-transcript"
        >
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-mono-oak">You</p>
          <p className="mt-1 text-[13px] text-white/85 leading-snug">
            {transcript || <span className="text-white/35 italic">Listening…</span>}
          </p>
        </div>
      )}

      {/* Error display */}
      {recError && (
        <div
          className="w-full max-w-md rounded-xl border border-rose-500/30 bg-rose-950/30 p-3 text-[12px] text-rose-300 flex items-center gap-2"
          data-testid="voice-error"
        >
          <AlertCircle size={14} className="flex-shrink-0" />
          <span>{recError}</span>
        </div>
      )}

      {/* Last answer (markdown rendered, useful when TTS is muted by the OS) */}
      {lastTurn?.answer && (
        <div
          className="w-full max-w-md rounded-2xl border border-[#00FF9D]/20 bg-[#00FF9D]/[0.04] p-4"
          data-testid="voice-answer"
        >
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#9bffcc] font-mono-oak mb-2">OAK</p>
          <div className="text-[13px] text-white/85 leading-relaxed">
            <LessonMarkdown markdown={lastTurn.answer} />
          </div>
        </div>
      )}
    </div>
  );
}

// Local copy of the paragraph-preserving markdown stripper. Kept inline
// to avoid importing LiveClassroomPanel's internals — same intent, just
// strips formatting before TTS narrates.
function _stripMd(md) {
  if (!md) return "";
  return md
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^#+\s*/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}
